import { Box, Grid, Grow, Paper, Switch, TextField, Tooltip, Typography } from '@mui/material'
import { SnackBarContext } from 'components'
import { theme } from 'config'
import { inject, observer } from 'mobx-react'
import { Course, Quiz } from 'models'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RoutingStore } from 'stores'
import { TaskType } from 'types'

export interface IQuizSettingsProps {
  routingStore?: RoutingStore,
  course?:       Course,
}

export const QuizSettings = inject('routingStore')(observer((props: IQuizSettingsProps) => {
    const { course } = props;
    const { t }      = useTranslation();
    const quiz: Quiz = course?.getCurStep?.quiz;
    const tasks      = course?.getCurStep?.quiz.tasks;
    const { toast }  = React.useContext(SnackBarContext);

    useEffect(() => {
      const modifySettings = async () => {
        await quiz.set('show_results', false, true);
        await quiz.set('is_control_quiz', false, true);
      }
      if(quiz.hasColumnTasks){
        modifySettings();
      }
    }, [quiz.hasColumnTasks])

    if (!quiz) return null;

    return (
      <Paper elevation={0} sx={{ padding: '3rem', borderRadius: '12px' }}>
        <Grid container>
          <Typography fontWeight='bold' textTransform='uppercase'>
            {t('quiz_settings_title')}
          </Typography>
          <Grid
            container
            gap            = {3}
            mb             = {2}
            alignItems     = 'center'
            justifyContent = 'space-between'
          >
            <Grid item xs = 'auto'>
              <Typography>{t('nr_of_questions_shown')}: </Typography>
            </Grid>
            <Grid item xs = 'auto'>
              <TextField
                fullWidth
                disabled    = {!course?.canEdit}
                type        = 'number'
                variant     = 'outlined'
                placeholder = {t('number_of_questions')}
                value       = {quiz.num_tasks}
                sx          = {{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: theme.palette.background.default,
                    width:           '8rem',
                  },
                }}
                inputProps={{
                  min: 1,
                  max: tasks.length,
                }}
                onChange = {(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  quiz.set('num_tasks', e.target.value, true)
                }
              />
            </Grid>
          </Grid>
          <Grow in={!quiz.is_control_quiz} unmountOnExit>
            <Grid
              container
              gap            = {3}
              alignItems     = 'center'
              justifyContent = 'space-between'
            >
              <Grid item xs = 'auto'>
                <Typography>{t('min_nr_correct_answers')}: </Typography>
              </Grid>
              <Grid item xs = 'auto'>
                <TextField
                  fullWidth
                  disabled    = {!course?.canEdit}
                  type        = 'number'
                  variant     = 'outlined'
                  placeholder = {t('completion_limit')}
                  sx          = {{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: theme.palette.background.default,
                      width:           '8rem',
                    },
                  }}
                  inputProps = {{
                    min: 0,
                    max: quiz.num_tasks,
                  }}
                  value    = {quiz.num_correct}
                  onChange = {(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    quiz.set('num_correct', e.target.value, true)
                  }
                />
              </Grid>
            </Grid>
          </Grow>
          <Grid
            container
            gap            = {3}
            alignItems     = 'center'
            justifyContent = 'space-between'
          >
            <Grid item xs = 'auto'>
              <Typography>{t('control_questions')}: </Typography>
            </Grid>
            <Grid item xs = 'auto'>
              <Tooltip title={t('control_questions_tip')} arrow>
                <Box>
                  <Switch 
                    sx       = {{mr: '2.5rem'}}
                    checked  = {quiz.is_control_quiz}
                    disabled = {!course?.canEdit || quiz.hasColumnTasks}
                    onChange = {async (e) => {
                      if(!quiz.patch({
                        final:           false,
                        num_correct:     0,
                        is_control_quiz: !quiz.is_control_quiz
                      }, true)) {
                        console.error("Failed to update quiz");
                        toast(t('failed_change_quiz'), "error");
                      }
                    }}
                  />
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
          {!quiz.isCurrentTaskColumn && <Grow in={!quiz.is_control_quiz} unmountOnExit >
            <Grid
              container
              gap            = {3}
              alignItems     = 'center'
              justifyContent = 'space-between'
            >
              <Grid item xs = 'auto'>
                <Typography>{t('final_exam')}: </Typography>
              </Grid>
              <Grid item xs = 'auto'>
                <Tooltip title={t('course_has_quiz')} arrow open={(course?.canEdit && course.hasFinalQuiz && !quiz.final) ? undefined : false}>
                  <Box sx={{cursor: (course.hasFinalQuiz && !quiz.final) ? "not-allowed" : undefined}}>
                    <Switch 
                      sx       = {{mr: '2.5rem'}}
                      checked  = {quiz.final}
                      disabled = {!course?.canEdit || (course.hasFinalQuiz && !quiz.final)}
                      onChange = {async (e) => {
                        const is_final = !quiz.final;
                        if(!quiz.set("final", is_final, true)) {
                          console.error("Failed to set quiz to final");
                          toast(t('failed_change_quiz'), "error");
                        } else {
                          if(is_final) {
                            course.getCurStep.set('mandatory', true, false); // The backend enforces the patch
                            toast(t('quiz_now_mandatory'), "success");
                          }
                        }
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </Grow>}
          <Grid
            container
            gap            = {3}
            alignItems     = 'center'
            justifyContent = 'space-between'
          >
            <Grid item xs = 'auto'>
              <Typography>{t('show_results_page')}: </Typography>
            </Grid>
            <Grid item xs = 'auto'>
              <Tooltip title={t('show_results_page')} arrow>
                <Box>
                  <Switch
                    sx       = {{mr: '2.5rem'}}
                    checked  = {quiz.showResults}
                    disabled = {!course?.canEdit || quiz.hasColumnTasks}
                    onChange = {async (e) => {
                      const show_results = !quiz.show_results;
                      const result = await quiz.set("show_results", show_results, true);
                      if (!result) {
                        console.error("Failed to set quiz to show_results");
                        toast(t('failed_change_quiz'), "error");
                      } else {
                        // Handle success
                        if (show_results) {
                          toast(t('quiz_show_results'), "success");
                        } else {
                          toast(t('quiz_not_show_results'), "success");
                        }
                      }
                    }}
                  />
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
          {!quiz.isValid && (
            <Box
              sx = {{ borderRadius: '6px', backgroundColor: 'rgba(171, 35, 40, 0.2)' }}
              p  = {1}
              mt = {2}
            >
              <Typography color = {theme.palette.error.main}>
                {t(quiz.getValidationError, { ns: 'validation' })}
              </Typography>
            </Box>
          )}
        </Grid>
      </Paper>
    )
  })
)
